import { Viewer } from "./viewer";
import { Deferred } from "core/Misc/deferred";
import { Logger } from "core/Misc/logger";
import { SuspendRenderingWhenOffscreen } from "../offscreenRenderingSuspension";
const DefaultCanvasViewerOptions = {
    antialias: true,
    adaptToDeviceRatio: true,
    enableAllFeatures: true,
    setMaximumLimits: true,
};
/**
 * Chooses a default engine for the current browser environment.
 * @returns The default engine to use.
 */
export function GetDefaultEngine() {
    // First check for WebGPU support.
    if ("gpu" in navigator) {
        // For now, only use WebGPU with chromium-based browsers.
        // WebGPU can be enabled in other browsers once they are fully functional and the performance is at least as good as WebGL.
        if ("chrome" in window) {
            return "WebGPU";
        }
    }
    return "WebGL";
}
/**
 * @internal
 */
export async function CreateViewerForCanvas(canvas, options = {}) {
    const detailsDeferred = new Deferred();
    options = new Proxy(options, {
        get(target, prop) {
            switch (prop) {
                case "antialias":
                    return target.antialias ?? DefaultCanvasViewerOptions.antialias;
                case "adaptToDeviceRatio":
                    return target.adaptToDeviceRatio ?? DefaultCanvasViewerOptions.adaptToDeviceRatio;
                case "enableAllFeatures":
                    return target.enableAllFeatures ?? DefaultCanvasViewerOptions.enableAllFeatures;
                case "setMaximumLimits":
                    return target.setMaximumLimits ?? DefaultCanvasViewerOptions.setMaximumLimits;
                case "onInitialized":
                    return (details) => {
                        target.onInitialized?.(details);
                        detailsDeferred.resolve(details);
                    };
                default:
                    return target[prop];
            }
        },
    });
    const disposeActions = [];
    // Create an engine instance.
    let engine;
    switch (options.engine ?? GetDefaultEngine()) {
        case "WebGPU": {
            const { WebGPUEngine } = await import("core/Engines/webgpuEngine");
            const webGPUEngine = new WebGPUEngine(canvas, options);
            try {
                await webGPUEngine.initAsync();
                engine = webGPUEngine;
                break;
            }
            catch {
                Logger.Warn("Failed to initialize WebGPU engine. Falling back to WebGL.");
            }
        }
        // eslint-disable-next-line no-fallthrough
        case "WebGL": {
            const { Engine } = await import("core/Engines/engine");
            engine = new Engine(canvas, undefined, options);
            break;
        }
    }
    if (options.onFaulted) {
        const onFaulted = options.onFaulted;
        const contextLostObserver = engine.onContextLostObservable.addOnce(() => {
            onFaulted(new Error("The engine context was lost."));
        });
        disposeActions.push(() => contextLostObserver.remove());
    }
    // Instantiate the Viewer with the engine and options.
    const viewerClass = options?.viewerClass ?? Viewer;
    const viewer = new viewerClass(engine, options);
    {
        const details = await detailsDeferred.promise;
        // If the canvas is resized, note that the engine needs a resize, but don't resize it here as it will result in flickering.
        let needsResize = false;
        const resizeObserver = new ResizeObserver(() => {
            needsResize = true;
            details.markSceneMutated();
        });
        resizeObserver.observe(canvas);
        disposeActions.push(() => resizeObserver.disconnect());
        // Resize if needed right before rendering the Viewer scene to avoid any flickering.
        const beforeRenderObserver = details.scene.onBeforeRenderObservable.add(() => {
            if (needsResize) {
                engine.resize();
                needsResize = false;
            }
        });
        disposeActions.push(() => beforeRenderObserver.remove());
        // If the canvas is not visible, suspend rendering.
        disposeActions.push(SuspendRenderingWhenOffscreen(canvas, () => details.suspendRendering()).dispose);
    }
    disposeActions.push(viewer.dispose.bind(viewer));
    disposeActions.push(() => engine.dispose());
    // Override the Viewer's dispose method to add in additional cleanup.
    viewer.dispose = () => disposeActions.forEach((dispose) => dispose());
    return viewer;
}
//# sourceMappingURL=viewerFactory.js.map